﻿import Collapse from 'bootstrap/js/dist/collapse';
import { getDialogQuestions, submitDialogSurvey, DialogQuestion, SurveyAnswer, DialogClosedError } from '../../../services/network';
import { generateUserHash } from '../../../services/hash';
import { ContactSettings, initContactSection, readContactSection, renderContactSection, resetContactSection } from './contactDetails';

const CONTACT_PREFIX = 'survey';

let collapse: Collapse | null = null;

export async function initDynamicSurveyDialog(isOpen: boolean = true, contact: ContactSettings | null = null): Promise<void> {
    const slug = (window as any).dialogSlug;
    if (!slug) {
        console.log('No dialog slug found, skipping dynamic survey');
        return;
    }

    const container = document.getElementById('dynamicSurveyContainer');
    if (!container) {
        console.log('No survey container found');
        return;
    }

    try {
        const questionsData = await getDialogQuestions(slug);

        if (questionsData.questions.length === 0) {
            container.innerHTML = '';
            return;
        }

        // A closed dialog accepts no responses, so no contact details are asked for either
        const contactSettings = isOpen ? contact : null;
        container.innerHTML = renderSurveyForm(questionsData.dialogTitle, questionsData.questions, isOpen, contactSettings);

        initSurveyInteractions(slug, questionsData.questions, isOpen, contactSettings !== null);
    } catch (error) {
        console.error('Error loading survey questions:', error);
        container.innerHTML = '';
    }
}

function renderSurveyForm(dialogTitle: string, questions: DialogQuestion[], isOpen: boolean, contact: ContactSettings | null): string {
    const questionsHtml = questions.map(q => renderQuestion(q)).join('');
    const contactHtml = contact
        ? `${renderContactSection(contact, CONTACT_PREFIX)}
           <div id="surveyContactError" class="alert alert-danger d-none mt-2 mb-0 small" role="alert"></div>`
        : '';

    // A closed dialog shows the questions but accepts no more responses
    const footerHtml = isOpen
        ? `<div class="d-flex justify-content-between">
                                <button type="submit" class="btn btn-primary">Spara</button>
                                <button type="reset" class="btn btn-outline-secondary">Rensa</button>
                            </div>`
        : `<div class="alert alert-secondary mb-0 text-center" role="alert">
                                Dialogen är stängd och tar inte emot fler svar.
                            </div>`;

    return `
        <div id="dynamicSurveyModal" class="ms-0 w-100" style="z-index: 1050; max-width: 400px; min-width: 0;">
            <div class="card survey-card">
                <div class="card-header d-flex justify-content-between align-items-center" style="background-color: #23972A; color: White">
                    <span>Din röst</span>
                    <button id="surveyCollapseToggleBtn" class="btn btn-sm" type="button" style="box-shadow:none; color:White;border: none !important;outline: none !important;background-color: transparent !important;">
                        <span class="material-symbols-outlined">keyboard_double_arrow_up</span>
                    </button>
                </div>
                <div id="surveyFormBody" class="collapse show">
                    <form id="dynamicSurveyForm" class="d-flex flex-column">
                        <div class="card-body survey-questions-scroll">
                            ${questionsHtml}
                            ${contactHtml}
                        </div>
                        <div class="card-footer bg-white border-top">
                            ${footerHtml}
                            <div id="surveyThanks" class="mt-2 alert alert-success d-none text-center mb-0" role="alert">
                                Tack för din åsikt!
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    `;
}

function renderQuestion(question: DialogQuestion): string {
    const emoji = question.emoji ? `${question.emoji} ` : '';
    const required = question.isRequired ? '*' : '';

    switch (question.questionType) {
        case 'text':
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    <input type="text" class="form-control" name="q_${question.questionId}" ${question.isRequired ? 'required' : ''} />
                </div>
            `;

        case 'textarea':
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    <textarea class="form-control" name="q_${question.questionId}" rows="2" ${question.isRequired ? 'required' : ''}></textarea>
                </div>
            `;

        case 'select':
            const selectOptions = question.options?.map(opt => `<option value="${opt}">${opt}</option>`).join('') || '';
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    <select class="form-select" name="q_${question.questionId}" ${question.isRequired ? 'required' : ''}>
                        <option selected disabled value="">Valj...</option>
                        ${selectOptions}
                    </select>
                </div>
            `;

        case 'radio':
            const radioOptions = question.options?.map((opt, i) => `
                <div class="form-check form-check-inline">
                    <input class="form-check-input" type="radio" name="q_${question.questionId}" id="q_${question.questionId}_${i}" value="${opt}" ${question.isRequired ? 'required' : ''}>
                    <label class="form-check-label" for="q_${question.questionId}_${i}">${opt}</label>
                </div><br />
            `).join('') || '';
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    ${radioOptions}
                </div>
            `;

        case 'checkbox':
            const checkboxOptions = question.options?.map((opt, i) => `
                <div class="form-check">
                    <input class="form-check-input" type="checkbox" name="q_${question.questionId}" id="q_${question.questionId}_${i}" value="${opt}">
                    <label class="form-check-label" for="q_${question.questionId}_${i}">${opt}</label>
                </div>
            `).join('') || '';
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    ${checkboxOptions}
                </div>
            `;

        case 'scale':
            const min = question.scaleMin || 1;
            const max = question.scaleMax || 10;
            const scaleButtons: string[] = [];
            for (let i = min; i <= max; i++) {
                scaleButtons.push(`
                    <input type="radio" class="btn-check" name="q_${question.questionId}" id="q_${question.questionId}_${i}" value="${i}" ${question.isRequired ? 'required' : ''}>
                    <label class="btn btn-outline-primary btn-sm" for="q_${question.questionId}_${i}">${i}</label>
                `);
            }
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    <div class="d-flex justify-content-between mb-1">
                        <small class="text-muted">${question.scaleMinLabel || min}</small>
                        <small class="text-muted">${question.scaleMaxLabel || max}</small>
                    </div>
                    <div class="btn-group w-100" role="group">
                        ${scaleButtons.join('')}
                    </div>
                </div>
            `;

        default:
            return `
                <div class="mb-2">
                    <h6>${emoji}${question.questionText}${required}</h6>
                    <input type="text" class="form-control" name="q_${question.questionId}" />
                </div>
            `;
    }
}

function initSurveyInteractions(slug: string, questions: DialogQuestion[], isOpen: boolean, hasContact: boolean): void {
    const form = document.getElementById('dynamicSurveyForm') as HTMLFormElement;
    const collapseEl = document.getElementById('surveyFormBody');
    const toggleBtn = document.getElementById('surveyCollapseToggleBtn');
    const thanksDiv = document.getElementById('surveyThanks');
    const contactErrorDiv = document.getElementById('surveyContactError');

    if (collapseEl) {
        collapse = new Collapse(collapseEl, { toggle: false });
        setCollapseForScreen();
        window.addEventListener('resize', setCollapseForScreen);
    }

    if (toggleBtn && collapse) {
        toggleBtn.addEventListener('click', () => {
            collapse?.toggle();
        });
    }

    if (!isOpen) {
        // Lock the fields so a closed dialog cannot be filled in
        form?.querySelectorAll<HTMLInputElement>('input, select, textarea')
            .forEach(field => field.disabled = true);
        return;
    }

    if (hasContact) {
        initContactSection(CONTACT_PREFIX);
        form?.addEventListener('reset', () => {
            resetContactSection(CONTACT_PREFIX);
            contactErrorDiv?.classList.add('d-none');
        });
    }

    if (form) {
        form.addEventListener('submit', async (e) => {
            e.preventDefault();

            const contactResult = hasContact ? readContactSection(CONTACT_PREFIX) : {};
            if (contactErrorDiv) {
                contactErrorDiv.textContent = contactResult.error ?? '';
                contactErrorDiv.classList.toggle('d-none', !contactResult.error);
            }
            if (contactResult.error) return;

            const formData = new FormData(form);
            const answers: SurveyAnswer[] = [];

            questions.forEach(q => {
                const fieldName = `q_${q.questionId}`;

                if (q.questionType === 'checkbox') {
                    const values = formData.getAll(fieldName) as string[];
                    if (values.length > 0) {
                        answers.push({
                            questionId: q.questionId,
                            answerText: values.join(', ')
                        });
                    }
                } else if (q.questionType === 'scale') {
                    const value = formData.get(fieldName) as string;
                    if (value) {
                        answers.push({
                            questionId: q.questionId,
                            answerValue: parseInt(value)
                        });
                    }
                } else {
                    const value = formData.get(fieldName) as string;
                    if (value) {
                        answers.push({
                            questionId: q.questionId,
                            answerText: value
                        });
                    }
                }
            });

            try {
                const userHash = generateUserHash();
                await submitDialogSurvey(slug, userHash, answers, contactResult.contact);

                if (thanksDiv) {
                    thanksDiv.classList.remove('d-none');
                    setTimeout(() => {
                        thanksDiv.classList.add('d-none');
                    }, 3000);
                }

                form.reset();
            } catch (error) {
                if (error instanceof DialogClosedError) {
                    alert('Dialogen tar inte emot svar just nu.');
                    return;
                }
                console.error('Error submitting survey:', error);
                alert('Kunde inte skicka enkäten. Försök igen.');
            }
        });
    }
}

function setCollapseForScreen(): void {
    if (!collapse) return;

    if (window.innerWidth < 768) {
        collapse.hide();
    } else {
        collapse.show();
    }
}
